import Logo from "components/Logo";
import SEO from "components/SEO";
import { env } from "utils/env";
import LoadingView from "./views/LoadingView";
import ProcessingView from "./views/ProcessingView";
import ReadyView from "./views/ReadyView";
import ExpiredView from "./views/ExpiredView";
import MessageView from "./views/MessageView";
import "./downloads.css";

/*
 * Presentational core of the downloads page — renders every state off plain
 * props so it is driven identically by the live query wrapper (index.jsx) and
 * by the fidelity harness with fixtures. See index.jsx for the page overview.
 */

// The Movebooth mark + the "Delivered by Movebooth" footer wrap every non-loading
// state; loading owns its own skeleton (logo + card) and needs no chrome.
const Shell = ({ children }) => (
  <>
    <span className="page-logo anim">
      <Logo withText fill="var(--logo-ink)" />
    </span>
    {children}
    <footer className="page-footer">
      <span>Delivered by</span> <a href="https://movebooth.com">Movebooth</a>
    </footer>
  </>
);

const DownloadsView = ({ token, isLoading, data, error, onRetry }) => {
  // Part anchors point straight at the rails endpoint, whose 302 → presigned
  // S3 URL (Content-Disposition: attachment) drives the actual file download.
  const apiRoot = env.VITE_API_ROOT || "";
  const buildPartUrl = (n) =>
    `${apiRoot}/v3/downloads/${encodeURIComponent(token)}/parts/${n}`;

  let stateName;
  let inner;

  if (isLoading) {
    stateName = "loading";
    inner = <LoadingView />;
  } else if (error) {
    const code = error.statusCode;
    if (code === 410) {
      stateName = "expired";
      inner = (
        <Shell>
          <ExpiredView token={token} />
        </Shell>
      );
    } else if (code === 404) {
      stateName = "notfound";
      inner = (
        <Shell>
          <MessageView variant="notfound" />
        </Shell>
      );
    } else {
      stateName = "error";
      inner = (
        <Shell>
          <MessageView variant="error" onRetry={onRetry} />
        </Shell>
      );
    }
  } else if (data?.status === "processing") {
    stateName = "processing";
    inner = (
      <Shell>
        <ProcessingView data={data} />
      </Shell>
    );
  } else if (data?.status === "partial") {
    stateName = "partial";
    inner = (
      <Shell>
        <ReadyView data={data} variant="partial" buildPartUrl={buildPartUrl} />
      </Shell>
    );
  } else if (data?.status === "ready") {
    stateName = data.part_count === 1 ? "ready-single" : "ready-multi";
    inner = (
      <Shell>
        <ReadyView
          data={data}
          variant={data.part_count === 1 ? "single" : "multi"}
          buildPartUrl={buildPartUrl}
        />
      </Shell>
    );
  } else {
    // Unknown / error status from the API — treat as the retryable error state.
    stateName = "error";
    inner = (
      <Shell>
        <MessageView variant="error" onRetry={onRetry} />
      </Shell>
    );
  }

  return (
    <div className="mb-downloads">
      <SEO title="Your download" noIndex />
      <div className="page">
        <div className="state active" data-state={stateName}>
          {inner}
        </div>
      </div>
    </div>
  );
};

export default DownloadsView;
